import { useEffect, useState } from "react";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Trash2, Edit, Shield, User as UserIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UsersPage({ user, logout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "user"
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (e) {
      console.error("Error fetching users:", e);
      if (e.response?.status === 403) {
        toast.error("Non hai i permessi per visualizzare gli utenti");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error("Nome ed email sono obbligatori");
      return;
    }
    
    if (!editingUser && !formData.password) {
      toast.error("La password è obbligatoria per nuovi utenti");
      return;
    }

    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData, { withCredentials: true });
        toast.success("Utente aggiornato");
      } else {
        await axios.post(`${API}/users`, formData, { withCredentials: true });
        toast.success("Utente creato");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (e) {
      console.error("Error saving user:", e);
      if (e.response?.data?.detail) {
        toast.error(e.response.data.detail);
      } else {
        toast.error("Errore nel salvataggio");
      }
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo utente?")) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success("Utente eliminato");
      fetchUsers();
    } catch (e) {
      console.error("Error deleting user:", e);
      if (e.response?.data?.detail) {
        toast.error(e.response.data.detail);
      } else {
        toast.error("Errore nell'eliminazione");
      }
    }
  };

  const openEditDialog = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      name: userToEdit.name,
      email: userToEdit.email,
      password: "",
      role: userToEdit.role || "user"
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "user"
    });
  };

  // Check if current user is admin
  if (user?.role !== "admin") {
    return (
      <PageLayout user={user} logout={logout} testId="users-page-denied" className="overflow-auto">
          <div className="p-8 max-w-4xl mx-auto">
            <Card>
              <CardContent className="pt-6 text-center">
                <Shield className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-slate-900 mb-2">Accesso Negato</h2>
                <p className="text-slate-600">Solo gli amministratori possono gestire gli utenti.</p>
              </CardContent>
            </Card>
          </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout user={user} logout={logout} testId="users-page" className="overflow-auto">
        <div className="p-8 max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Gestione Utenti</h1>
              <p className="text-slate-600 mt-1">Crea, modifica ed elimina utenti del CRM</p>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white" data-testid="new-user-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Utente
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingUser ? "Modifica Utente" : "Nuovo Utente"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nome utente"
                      data-testid="user-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@esempio.it"
                      data-testid="user-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">
                      Password {editingUser ? "(lascia vuoto per non modificare)" : "*"}
                    </Label>
                    <Input
                      id="password"
                      type="text"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={editingUser ? "••••••••" : "Password"}
                      data-testid="user-password"
                    />
                  </div>
                  <div>
                    <Label>Ruolo</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                    >
                      <SelectTrigger data-testid="user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">Utente</SelectItem>
                        <SelectItem value="admin">Amministratore</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Annulla
                    </Button>
                    <Button type="submit" className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white" data-testid="save-user-btn">
                      {editingUser ? "Salva Modifiche" : "Crea Utente"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Users List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Utenti ({users.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Nessun utente presente</p>
                ) : (
                  <div className="space-y-3">
                    {users.map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                        data-testid={`user-row-${u.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            u.role === "admin" ? "bg-amber-100" : "bg-slate-200"
                          }`}>
                            {u.role === "admin" ? (
                              <Shield className="w-5 h-5 text-amber-600" />
                            ) : (
                              <UserIcon className="w-5 h-5 text-slate-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            u.role === "admin" 
                              ? "bg-amber-100 text-amber-700" 
                              : "bg-slate-200 text-slate-600"
                          }`}>
                            {u.role === "admin" ? "Admin" : "Utente"}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(u)}
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(u.id)}
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              disabled={u.id === user?.id}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
    </PageLayout>
  );
}
